import { useState, type MouseEvent } from "react";
import { Button, Popover } from 'antd';
import clsx from 'clsx';
import dayjs, { type Dayjs } from 'dayjs';
import { CalendarIcon, CloseIcon, PlusIconBlack } from '@/shared/assets/svg/icons';
import './date-range-filter.scss';

type ActiveField = 'start' | 'end';

export type RangeValue = [Dayjs | null, Dayjs | null] | null;

export interface DateRangeFilterProps {
  value?: RangeValue;
  defaultValue?: RangeValue;
  onChange?: (value: RangeValue) => void;
  placeholder?: string;
  allowClear?: boolean;
  disabled?: boolean;
}

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

type CalendarDay = { date: Dayjs; isOutside: boolean };

function buildCalendarWeeks(viewDate: Dayjs): CalendarDay[][] {
  const startOfMonth = viewDate.startOf('month');

  // Russian calendar: Mon=0 ... Sun=6
  const dow = startOfMonth.day();
  const startDow = dow === 0 ? 6 : dow - 1;

  const days: CalendarDay[] = [];

  for (let i = startDow - 1; i >= 0; i -= 1) {
    days.push({ date: startOfMonth.subtract(i + 1, 'day'), isOutside: true });
  }

  const daysInMonth = viewDate.daysInMonth();
  for (let d = 1; d <= daysInMonth; d += 1) {
    days.push({ date: viewDate.date(d), isOutside: false });
  }

  while (days.length % 7 !== 0) {
    const last = days[days.length - 1].date;
    days.push({ date: last.add(1, 'day'), isOutside: true });
  }

  const weeks: CalendarDay[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return weeks;
}

function isSameDay(a: Dayjs | null | undefined, b: Dayjs | null | undefined): boolean {
  if (!a || !b) return false;
  return a.isSame(b, 'day');
}

function isStrictlyBetween(date: Dayjs, a: Dayjs | null, b: Dayjs | null): boolean {
  if (!a || !b) return false;
  const lo = a.isBefore(b, 'day') ? a : b;
  const hi = a.isBefore(b, 'day') ? b : a;
  return date.isAfter(lo, 'day') && date.isBefore(hi, 'day');
}

function resolveInitialApplied(
  defaultValue: RangeValue | undefined,
): [Dayjs | null, Dayjs | null] {
  if (!defaultValue) return [null, null];
  return [defaultValue[0] ?? null, defaultValue[1] ?? null];
}

export const DateRangeFilter = ({
  value,
  defaultValue,
  onChange,
  placeholder = 'Дата акта',
  allowClear = true,
  disabled = false,
}: DateRangeFilterProps) => {
  const isControlled = value !== undefined;

  const [internalApplied, setInternalApplied] = useState<[Dayjs | null, Dayjs | null]>(
    () => resolveInitialApplied(defaultValue),
  );

  const appliedRange: [Dayjs | null, Dayjs | null] = isControlled
    ? [value?.[0] ?? null, value?.[1] ?? null]
    : internalApplied;

  const [open, setOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [activeField, setActiveField] = useState<ActiveField>('start');
  const [viewDate, setViewDate] = useState<Dayjs>(() => dayjs().startOf('month'));
  const [hoverDate, setHoverDate] = useState<Dayjs | null>(null);

  const initDraft = () => {
    const draft: [Dayjs | null, Dayjs | null] = [appliedRange[0], appliedRange[1]];
    setDraftRange(draft);
    setViewDate((draft[0] ?? draft[1] ?? dayjs()).startOf('month'));
    if (!draft[0]) {
      setActiveField('start');
    } else if (!draft[1]) {
      setActiveField('end');
    } else {
      setActiveField('start');
    }
  };

  const handleClose = () => {
    setOpen(false);
    setHoverDate(null);
  };

  const handleOpenChange = (vis: boolean) => {
    if (disabled) return;
    if (vis) {
      initDraft();
      setOpen(true);
    } else {
      handleClose();
    }
  };

  const handleClear = (e: MouseEvent) => {
    e.stopPropagation();
    if (!isControlled) setInternalApplied([null, null]);
    onChange?.([null, null]);
  };

  const handleOk = () => {
    const [s, e] = draftRange;
    if (!s || !e) return;
    if (!isControlled) setInternalApplied([s, e]);
    onChange?.([s, e]);
    handleClose();
  };

  const handleDayClick = (date: Dayjs) => {
    if (activeField === 'start') {
      const newEnd =
        draftRange[1] && date.isAfter(draftRange[1], 'day') ? null : draftRange[1];
      setDraftRange([date, newEnd]);
      setActiveField('end');
    } else if (draftRange[0] && date.isBefore(draftRange[0], 'day')) {
      setDraftRange([date, null]);
      setActiveField('end');
    } else {
      // Range is now complete — clear hover to prevent stale preview
      setDraftRange([draftRange[0], date]);
      setHoverDate(null);
    }
  };

  const handleToday = () => {
    const today = dayjs();
    setViewDate(today.startOf('month'));
    if (activeField === 'start') {
      const newEnd =
        draftRange[1] && today.isAfter(draftRange[1], 'day') ? null : draftRange[1];
      setDraftRange([today, newEnd]);
      setActiveField('end');
    } else if (draftRange[0] && today.isBefore(draftRange[0], 'day')) {
      setDraftRange([today, null]);
      setActiveField('end');
    } else {
      setDraftRange([draftRange[0], today]);
    }
  };

  const goToPrevYear = () => setViewDate((d) => d.subtract(1, 'year'));
  const goToPrevMonth = () => setViewDate((d) => d.subtract(1, 'month'));
  const goToNextMonth = () => setViewDate((d) => d.add(1, 'month'));
  const goToNextYear = () => setViewDate((d) => d.add(1, 'year'));

  // ── Trigger derived state ──────────────────────────────────────────────────

  const [appliedStart, appliedEnd] = appliedRange;
  const hasValue = !!(appliedStart || appliedEnd);

  const chipText = (() => {
    if (appliedStart && appliedEnd) {
      return `${appliedStart.format('DD.MM.YYYY')} \u2014 ${appliedEnd.format('DD.MM.YYYY')}`;
    }
    if (appliedStart) return appliedStart.format('DD.MM.YYYY');
    return '';
  })();

  // ── Calendar state ─────────────────────────────────────────────────────────

  const [draftStart, draftEnd] = draftRange;
  const isOkEnabled = !!(draftStart && draftEnd);
  const weeks = buildCalendarWeeks(viewDate);
  const today = dayjs();

  const getDayMeta = (day: CalendarDay) => {
    const { date } = day;
    const isStart = isSameDay(date, draftStart);
    const isEnd = isSameDay(date, draftEnd);
    const isToday = isSameDay(date, today);

    // Preview mode: ONLY when start is set and end is NOT yet confirmed.
    // Once draftEnd is set the preview is fully off, regardless of activeField.
    const isPreviewMode = draftStart !== null && draftEnd === null && hoverDate !== null;
    const hoverIsForward = isPreviewMode && !hoverDate!.isBefore(draftStart!, 'day');

    const isHoverEnd = isPreviewMode && hoverIsForward && isSameDay(date, hoverDate);
    const isHoverRange =
      isPreviewMode && hoverIsForward && isStrictlyBetween(date, draftStart, hoverDate);

    // Confirmed range: only when both endpoints exist.
    const hasConfirmedRange = draftStart !== null && draftEnd !== null;
    const confirmedInRange =
      hasConfirmedRange && isStrictlyBetween(date, draftStart, draftEnd);

    // Extension connectors for start/end circles.
    const isStartRanged = isStart && (hasConfirmedRange || (isPreviewMode && hoverIsForward));
    const isEndRanged = isEnd && hasConfirmedRange;

    return {
      isStart,
      isEnd,
      isToday,
      isHoverEnd,
      isHoverRange,
      confirmedInRange,
      isStartRanged,
      isEndRanged,
    };
  };

  // ── Render helpers ─────────────────────────────────────────────────────────

  const renderTrigger = () => (
    <div
      className={clsx('drf-trigger', {
        'drf-trigger--active': hasValue,
        'drf-trigger--disabled': disabled,
      })}
    >
      <span className="drf-trigger__label">{placeholder}</span>
      {hasValue ? (
        <span className="drf-value-chip">
          <span className="drf-value-chip__text">{chipText}</span>
          {allowClear && (
            <button
              type="button"
              className="drf-value-chip__clear"
              aria-label="Очистить"
              onClick={handleClear}
            >
              <CloseIcon />
            </button>
          )}
        </span>
      ) : (
        <PlusIconBlack className="drf-plus-icon" />
      )}
    </div>
  );

  const renderRangeFields = () => (
    <div className="drf-fields">
      <span className="drf-fields__prefix">С</span>
      <button
        type="button"
        className={clsx('drf-field', { 'drf-field--active': activeField === 'start' })}
        onClick={() => setActiveField('start')}
      >
        <span className="drf-field__value">
          {draftStart ? draftStart.format('DD.MM.YYYY') : ''}
        </span>
        <span className="drf-field__icon">
          <CalendarIcon />
        </span>
      </button>
      <span className="drf-fields__separator">&mdash;</span>
      <span className="drf-fields__prefix">По</span>
      <button
        type="button"
        className={clsx('drf-field', { 'drf-field--active': activeField === 'end' })}
        onClick={() => setActiveField('end')}
      >
        <span className="drf-field__value">
          {draftEnd ? draftEnd.format('DD.MM.YYYY') : ''}
        </span>
        <span className="drf-field__icon">
          <CalendarIcon />
        </span>
      </button>
    </div>
  );

  const renderCalendar = () => (
    <div className="drf-calendar">
      <div className="drf-calendar__header">
        <button type="button" className="drf-nav-btn" onClick={goToPrevYear} title="Предыдущий год">
          «
        </button>
        <button type="button" className="drf-nav-btn" onClick={goToPrevMonth} title="Предыдущий месяц">
          ‹
        </button>
        <span className="drf-calendar__title">
          {MONTHS[viewDate.month()]} {viewDate.year()}
        </span>
        <button type="button" className="drf-nav-btn" onClick={goToNextMonth} title="Следующий месяц">
          ›
        </button>
        <button type="button" className="drf-nav-btn" onClick={goToNextYear} title="Следующий год">
          »
        </button>
      </div>

      <div className="drf-calendar__weekdays">
        {WEEKDAYS.map((wd) => (
          <span key={wd} className="drf-weekday">
            {wd}
          </span>
        ))}
      </div>

      <div className="drf-calendar__grid">
        {weeks.map((week) => (
          <div key={week[0].date.format('YYYY-MM-DD')} className="drf-week">
            {week.map((day) => {
              const meta = getDayMeta(day);
              return (
                <button
                  key={day.date.format('YYYY-MM-DD')}
                  type="button"
                  className={clsx('drf-day', {
                    'drf-day--outside': day.isOutside,
                    'drf-day--today': meta.isToday && !meta.isStart && !meta.isEnd && !meta.isHoverEnd,
                    'drf-day--start': meta.isStart,
                    'drf-day--start-ranged': meta.isStartRanged,
                    'drf-day--end': meta.isEnd,
                    'drf-day--end-ranged': meta.isEndRanged,
                    'drf-day--hover-end': meta.isHoverEnd,
                    'drf-day--in-range': meta.confirmedInRange,
                    'drf-day--hover-range': meta.isHoverRange,
                  })}
                  onClick={() => handleDayClick(day.date)}
                  onMouseEnter={() => setHoverDate(day.date)}
                  onMouseLeave={() => setHoverDate(null)}
                >
                  <span className="drf-day__num">{day.date.date()}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );

  const renderFooter = () => (
    <div className="drf-footer">
      <button type="button" className="drf-today-btn" onClick={handleToday}>
        Сегодня
      </button>
      <Button
        type="primary"
        size="small"
        disabled={!isOkEnabled}
        onClick={handleOk}
        className="drf-ok-btn"
      >
        OK
      </Button>
    </div>
  );

  const popupContent = (
    <div className="drf-popup">
      {renderRangeFields()}
      {renderCalendar()}
      {renderFooter()}
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      content={popupContent}
      trigger="click"
      placement="bottomLeft"
      overlayClassName="drf-popover-overlay"
      arrow={false}
    >
      {renderTrigger()}
    </Popover>
  );
};
